<template>
  <div class="news-detail-page">
    <!-- 阅读进度条 -->
    <div class="reading-progress" :style="{width: progress + '%'}"></div>

    <!-- 文章头部：深绿编辑刊头 -->
    <header class="article-hero">
      <div class="hero-inner">
        <el-breadcrumb separator="/" class="hero-breadcrumb">
          <el-breadcrumb-item :to="{ path: $lp('/') }">{{ $t('common.home') }}</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: $lp('/news') }">{{ $t('routes.news') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ $t('news.articleBody') }}</el-breadcrumb-item>
        </el-breadcrumb>

        <template v-if="news">
          <div class="hero-kicker">
            <span class="cat-chip">{{ getCategoryName(news.category) }}</span>
            <span class="read-time">{{ $t('news.readTime', { n: readingTime }) }}</span>
          </div>
          <h1 class="hero-title">{{ news.title }}</h1>
          <div class="hero-meta">
            <span class="meta-date">{{ formatDate(news.editTime, dateFormat) }}</span>
            <span class="meta-line"></span>
            <span class="meta-brand">{{ $t('news.metaBrand') }}</span>
          </div>
        </template>
      </div>
      <!-- 装饰：巨型衬线字水印 + 细纹 -->
      <div class="hero-watermark" aria-hidden="true">{{ $t('news.watermark') }}</div>
    </header>

    <div class="page-container">
      <!-- 正文不存在 -->
      <div class="article-empty" v-if="!news">
        <el-empty :description="$t('news.notExist')">
          <el-button @click="router.push($lp('/news'))">{{ $t('news.backToNews') }}</el-button>
        </el-empty>
      </div>

      <template v-else>
        <div class="article-layout">
          <!-- 侧栏目录（桌面端） -->
          <aside class="article-toc" v-if="toc.length > 0">
            <div class="toc-title">{{ $t('news.tocTitle') }}</div>
            <ul class="toc-list">
              <li
                  v-for="(item, index) in toc"
                  :key="item.id"
                  :class="{active: activeId === item.id}"
              >
                <a :href="'#' + item.id" @click.prevent="scrollTo(item.id)">
                  <i class="toc-no">{{ String(index + 1).padStart(2, '0') }}</i>
                  <span>{{ item.text }}</span>
                </a>
              </li>
            </ul>
            <div class="toc-rule"></div>
            <button class="toc-back" @click="router.push($lp('/news'))">
              ← {{ $t('news.backToList') }}
            </button>
          </aside>

          <!-- 正文卡片 -->
          <article class="article-card">
            <div class="news-content" v-html="news.content"></div>

            <div class="article-footer">
              <span class="footer-mark">END</span>
              <button class="back-btn" @click="router.push($lp('/news'))">{{ $t('news.backToList') }}</button>
            </div>
          </article>
        </div>

        <!-- 相关阅读 -->
        <section class="related-news" v-if="relatedNews.length > 0">
          <div class="section-head">
            <h2 class="section-title">{{ $t('news.keepReading') }}</h2>
            <span class="section-en">{{ $t('news.keepReadingEn') }}</span>
          </div>
          <div class="related-list">
            <div v-for="item in relatedNews" :key="item._id" class="related-item">
              <ArticleCard
                  :id="item._id"
                  :title="item.title"
                  :summary="getSummary(item.content, 80)"
                  :cover="item.cover"
                  :date="item.editTime"
                  :category="getCategoryName(item.category)"
                  type="news"
              />
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useNewsData } from '@/composables/useNewsData';
import { formatDate, getSummary } from '@/utils/helper';
import ArticleCard from '@/components/ArticleCard.vue';

export default {
  name: 'NewsDetailView',
  components: {
    ArticleCard
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const {t} = useI18n();
    // 按语言切换的静态新闻数据源（getNewsById 内部读当前 locale，computed 依赖自动建立）
    const {getNewsById, getRelatedNews, getCategoryName: getCategoryNameByLocale} = useNewsData();

    // 日期格式随语言切换（helper.formatDate 仅支持 YYYY/MM/DD token）
    const dateFormat = computed(() => t('common.dateFormat'));

    // 从静态数据中获取当前新闻
    const news = computed(() => getNewsById(route.params.id));

    // 相关新闻推荐（同分类优先）
    const relatedNews = computed(() =>
      news.value ? getRelatedNews(news.value._id, 3) : []
    );

    // 预计阅读时长（按正文净字数，约 400 字/分钟）
    const readingTime = computed(() => {
      if (!news.value) return 1;
      const text = news.value.content.replace(/<[^>]+>/g, '');
      return Math.max(1, Math.round(text.length / 400));
    });

    // 目录：渲染后从正文 h2 提取
    const toc = ref([]);
    const activeId = ref('');

    const buildToc = async () => {
      await nextTick();
      const content = document.querySelector('.news-content');
      if (!content) {
        toc.value = [];
        return;
      }
      const heads = [...content.querySelectorAll('h2')];
      heads.forEach((h, i) => {
        h.id = `sec-${i}`;
        // 图片懒加载，长文优化
        content.querySelectorAll('img').forEach(img => {
          img.loading = 'lazy';
        });
      });
      toc.value = heads.map((h, i) => ({id: `sec-${i}`, text: h.textContent.trim()}));
      if (toc.value.length > 0) activeId.value = toc.value[0].id;
    };

    const scrollTo = (id) => {
      document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    // 阅读进度 + 目录高亮
    const progress = ref(0);
    let observer = null;

    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      progress.value = max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0;
    };

    onMounted(() => {
      buildToc();
      window.addEventListener('scroll', onScroll, {passive: true});
      onScroll();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    });

    // 监听路由ID变化，从相关新闻点进另一篇时重建目录并回顶
    watch(() => route.params.id, () => {
      window.scrollTo(0, 0);
      buildToc();
    });

    watch(toc, () => {
      // 滚动监听目录高亮（IntersectionObserver）
      observer?.disconnect();
      if (toc.value.length === 0) return;
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) activeId.value = entry.target.id;
        });
      }, {rootMargin: '-20% 0px -70% 0px'});
      toc.value.forEach(({id}) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    // 获取分类名称
    const getCategoryName = (categoryId) => {
      return getCategoryNameByLocale(categoryId) || t('common.uncategorized');
    };

    return {
      news,
      relatedNews,
      toc,
      activeId,
      progress,
      readingTime,
      dateFormat,
      router,
      formatDate,
      getSummary,
      getCategoryName,
      scrollTo
    };
  }
}
</script>

<style lang="scss" scoped>
// ===== 编辑部调色板 =====
$forest: #17382a;        // 深林绿
$forest-deep: #0f271d;   // 更深的绿
$leaf: #3d8b57;          // 叶绿
$moss: #6b8f71;          // 苔绿
$paper: #f7f4ec;         // 米纸
$paper-warm: #f1ecdf;    // 暖纸
$ink: #26302b;           // 墨绿字色
$gold: #b8935a;          // 麦金
$serif: "Source Han Serif SC", "Noto Serif SC", "Songti SC", "STSong", "SimSun", serif;
$sans: "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;

.news-detail-page {
  background: $paper;
  // 纸张细纹
  background-image: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 28px,
      rgba(23, 56, 42, 0.025) 28px,
      rgba(23, 56, 42, 0.025) 29px
  );
  min-height: 100vh;
  padding-bottom: 90px;
}

// ===== 阅读进度条 =====
.reading-progress {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  width: 0;
  background: linear-gradient(90deg, $leaf, $gold);
  z-index: 2000;
  transition: width 0.1s linear;
}

// ===== 刊头：浅色编辑版 =====
.article-hero {
  position: relative;
  background: $paper;
  overflow: hidden;
  padding: calc(var(--navbar-height, 80px) + 28px) 0 60px;
  border-bottom: 1px solid rgba($forest, 0.12);

  // 顶部双线收边
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: repeating-linear-gradient(
        90deg,
        $forest 0 60px,
        transparent 60px 80px
    );
    opacity: 0.85;
  }

  .hero-inner {
    position: relative;
    z-index: 2;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .hero-breadcrumb {
    margin-bottom: 32px;
    animation: rise 0.6s ease both;

    // 与列表页 PageHeader 风格统一（el-breadcrumb 默认深色主题）
    ::v-deep .el-breadcrumb__inner {
      color: $ink;
      font-weight: normal;

      a, a:hover {
        color: $ink;
        text-decoration: none;
      }
    }

    ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner {
      color: $forest;
      font-weight: 600;
    }

    ::v-deep .el-breadcrumb__separator {
      color: rgba($forest, 0.35);
    }
  }

  .hero-kicker {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    animation: rise 0.6s 0.1s ease both;

    .cat-chip {
      font-family: $sans;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 4px;
      padding: 5px 14px 5px 16px;
      background: $forest;
      color: $paper;
    }

    .read-time {
      font-size: 12px;
      letter-spacing: 2px;
      color: $moss;
      font-variant-numeric: tabular-nums;
    }
  }

  .hero-title {
    font-family: $serif;
    font-weight: 700;
    font-size: clamp(28px, 4vw, 48px);
    line-height: 1.4;
    color: $forest;
    max-width: 22em;
    margin: 0 0 28px;
    animation: rise 0.7s 0.2s ease both;
  }

  .hero-meta {
    display: flex;
    align-items: center;
    gap: 18px;
    animation: rise 0.7s 0.32s ease both;

    .meta-date {
      font-family: $serif;
      font-size: 15px;
      color: $ink;
      letter-spacing: 1px;
      font-variant-numeric: tabular-nums;
    }

    .meta-line {
      width: 48px;
      height: 1px;
      background: $gold;
    }

    .meta-brand {
      font-size: 12px;
      letter-spacing: 3px;
      color: $moss;
    }
  }

  // 大型水印字（淡金/墨色）
  .hero-watermark {
    position: absolute;
    right: -20px;
    bottom: -40px;
    font-family: $serif;
    font-size: 320px;
    line-height: 1;
    font-weight: 700;
    color: rgba($forest, 0.05);
    z-index: 1;
    user-select: none;
    pointer-events: none;
  }
}

// ===== 正文布局 =====
.article-layout {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 48px;
  margin-top: 52px;
  align-items: start;
}

// ===== 侧栏目录 =====
.article-toc {
  position: sticky;
  top: calc(var(--navbar-height, 80px) + 32px);
  padding: 24px 20px 20px;
  background: rgba(#fff, 0.55);
  border: 1px solid rgba($forest, 0.1);
  border-top: 2px solid $forest;

  .toc-title {
    font-family: $serif;
    font-size: 15px;
    font-weight: 700;
    color: $forest;
    letter-spacing: 6px;
    margin-bottom: 18px;
  }

  .toc-list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      a {
        display: flex;
        gap: 10px;
        align-items: baseline;
        padding: 8px 0;
        text-decoration: none;
        color: $moss;
        font-size: 13px;
        line-height: 1.55;
        border-left: 2px solid transparent;
        padding-left: 10px;
        margin-left: -12px;
        transition: color 0.2s, border-color 0.2s, padding-left 0.2s;

        .toc-no {
          font-style: normal;
          font-family: $serif;
          font-size: 12px;
          color: rgba($gold, 0.7);
          font-variant-numeric: tabular-nums;
          flex-shrink: 0;
        }

        span {
          @media (min-width: 769px) {
            display: inline;
          }
        }
      }

      &.active a {
        color: $forest;
        border-left-color: $gold;
        padding-left: 16px;
        font-weight: 600;

        .toc-no {
          color: $gold;
        }
      }
    }
  }

  .toc-rule {
    height: 1px;
    background: linear-gradient(90deg, rgba($gold, 0.5), transparent);
    margin: 18px 0 14px;
  }

  .toc-back {
    font-size: 12px;
    letter-spacing: 1px;
    color: $moss;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s;

    &:hover {
      color: $forest;
    }
  }
}

// ===== 正文卡片 =====
.article-card {
  background: #fff;
  padding: 56px clamp(24px, 5vw, 72px) 44px;
  box-shadow: 0 24px 60px -30px rgba($forest, 0.35);
  border: 1px solid rgba($forest, 0.06);
  animation: rise 0.7s 0.4s ease both;
}

.news-content {
  font-size: 16.5px;
  line-height: 2.05;
  color: $ink;
  counter-reset: sec;

  ::v-deep(p) {
    margin: 0 0 1.6em;
  }

  // 首段导语
  ::v-deep(p:first-child) {
    font-family: $serif;
    font-size: 18px;
    line-height: 1.95;
    color: $forest;
    padding-left: 18px;
    border-left: 3px solid $gold;
  }

  // 章节标题：衬线 + 编号
  ::v-deep(h2) {
    counter-increment: sec;
    font-family: $serif;
    font-size: 23px;
    font-weight: 700;
    color: $forest;
    margin: 2.4em 0 1em;
    line-height: 1.5;
    scroll-margin-top: calc(var(--navbar-height, 80px) + 24px);
    display: flex;
    align-items: baseline;
    gap: 14px;

    &::before {
      content: counter(sec, decimal-leading-zero);
      font-size: 15px;
      color: $gold;
      letter-spacing: 1px;
      border-bottom: 2px solid $gold;
      padding-bottom: 3px;
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
    }
  }

  ::v-deep(h3) {
    font-family: $serif;
    font-size: 19px;
    font-weight: 700;
    color: $forest;
    margin: 1.8em 0 0.8em;

    &::before {
      content: '§';
      color: $leaf;
      margin-right: 10px;
      font-weight: 400;
    }
  }

  // 插图：版画式装裱
  ::v-deep(img) {
    display: block;
    max-width: min(100%, 640px);
    margin: 2.2em auto;
    padding: 8px;
    background: $paper-warm;
    border: 1px solid rgba($forest, 0.15);
    box-shadow: 0 12px 28px -18px rgba($forest, 0.45);
    box-sizing: border-box;
  }

  ::v-deep(strong) {
    color: $forest;
  }

  // 图注
  ::v-deep(p.caption) {
    text-align: center;
    font-family: $serif;
    font-size: 14px;
    color: $moss;
    letter-spacing: 1px;
    margin: -1.4em 0 2.4em;
  }
}

// ===== 文末 =====
.article-footer {
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1px solid rgba($forest, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;

  .footer-mark {
    font-size: 12px;
    letter-spacing: 6px;
    color: rgba($gold, 0.9);
  }

  .back-btn {
    font-size: 13px;
    letter-spacing: 2px;
    color: $forest;
    background: transparent;
    border: 1px solid rgba($forest, 0.35);
    padding: 9px 22px;
    cursor: pointer;
    transition: all 0.25s;

    &:hover {
      background: $forest;
      color: #fff;
      border-color: $forest;
    }
  }
}

// ===== 空状态 =====
.article-empty {
  margin-top: 60px;
  background: #fff;
  padding: 70px 20px;
  border: 1px solid rgba($forest, 0.08);
}

// ===== 相关阅读 =====
.related-news {
  margin-top: 72px;

  .section-head {
    display: flex;
    align-items: baseline;
    gap: 18px;
    margin-bottom: 26px;

    .section-title {
      font-family: $serif;
      font-size: 26px;
      font-weight: 700;
      color: $forest;
      margin: 0;
    }

    .section-en {
      font-size: 11px;
      letter-spacing: 4px;
      color: rgba($gold, 0.85);
    }
  }

  .related-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;

    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
}

// ===== 入场动画 =====
@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ===== 响应式 =====
@media (max-width: 1080px) {
  .article-layout {
    grid-template-columns: 1fr;
  }

  .article-toc {
    position: static;
    display: none;
  }

  .article-hero .hero-watermark {
    font-size: 200px;
    opacity: 0.6;
  }
}

@media (max-width: 768px) {
  .article-hero {
    padding-bottom: 42px;

    .hero-title {
      font-size: 24px;
    }
  }

  .article-card {
    padding: 32px 20px 32px;
  }

  .news-content {
    font-size: 15.5px;
    line-height: 1.95;

    ::v-deep(h2) {
      font-size: 20px;
    }
  }
}
</style>
